/**
 * GuidedTour — a lightweight, dependency-free spotlight tour.
 *
 * Pass an ordered list of steps, each targeting an element by CSS selector
 * (we use data-testid selectors). The tour dims the page, cuts a spotlight
 * around the current target, and shows a tooltip card with Back / Next / Skip.
 *
 * Steps whose target never appears are skipped gracefully. Completion is the
 * caller's concern — call onDone() to persist a "seen" flag.
 */
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PAD = 8; // spotlight padding around the target

const GuidedTour = ({ steps, open, onDone }) => {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);

  // Find the target element for a given step, polling briefly since dashboard
  // content loads async. Returns the element or null after a few tries.
  const findTarget = useCallback((selector) => {
    return document.querySelector(selector);
  }, []);

  // Advance to the first step (from `start`) whose target currently exists.
  const resolveFrom = useCallback((start, dir = 1) => {
    let i = start;
    while (i >= 0 && i < steps.length) {
      if (findTarget(steps[i].selector)) return i;
      i += dir;
    }
    return -1;
  }, [steps, findTarget]);

  const measure = useCallback((i) => {
    const step = steps[i];
    if (!step) return;
    const el = findTarget(step.selector);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Measure after the scroll settles.
    setTimeout(() => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }, 260);
  }, [steps, findTarget]);

  // When the tour opens, wait for the first resolvable step (poll up to ~2s).
  useEffect(() => {
    if (!open) return;
    let tries = 0;
    const timer = setInterval(() => {
      const i = resolveFrom(0, 1);
      if (i !== -1) {
        clearInterval(timer);
        setIndex(i);
        measure(i);
      } else if (++tries > 20) {
        clearInterval(timer);
        onDone?.();
      }
    }, 100);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the spotlight aligned on resize / scroll.
  useLayoutEffect(() => {
    if (!open) return;
    const onMove = () => {
      const el = findTarget(steps[index]?.selector);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, index, steps, findTarget]);

  if (!open || !rect) return null;

  const step = steps[index];
  const next = () => {
    const i = resolveFrom(index + 1, 1);
    if (i === -1) { onDone?.(); return; }
    setIndex(i);
    measure(i);
  };
  const back = () => {
    const i = resolveFrom(index - 1, -1);
    if (i === -1) return;
    setIndex(i);
    measure(i);
  };

  const spotlight = {
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
  };

  // Place the card below the target if there's room, else above.
  const belowRoom = window.innerHeight - (rect.top + rect.height);
  const placeBelow = belowRoom > 220;
  const cardTop = placeBelow ? rect.top + rect.height + PAD + 12 : undefined;
  const cardBottom = placeBelow ? undefined : window.innerHeight - (rect.top - PAD) + 12;
  const cardLeft = Math.min(
    Math.max(12, rect.left),
    window.innerWidth - 340,
  );

  const isLast = resolveFrom(index + 1, 1) === -1;
  const isFirst = resolveFrom(index - 1, -1) === -1;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="guided-tour">
      {/* Spotlight cutout — the huge box-shadow dims everything around it. */}
      <div
        className="fixed rounded-xl transition-all duration-300 pointer-events-none"
        style={{
          top: spotlight.top,
          left: spotlight.left,
          width: spotlight.width,
          height: spotlight.height,
          boxShadow: '0 0 0 9999px rgba(2, 6, 23, 0.72)',
          border: '2px solid rgba(37, 244, 238, 0.9)',
        }}
      />

      {/* Tooltip card */}
      <div
        className="fixed w-[320px] max-w-[calc(100vw-24px)] rounded-2xl bg-card border border-border shadow-2xl p-5 animate-in fade-in zoom-in-95 duration-200"
        style={{ top: cardTop, bottom: cardBottom, left: cardLeft }}
        data-testid="guided-tour-card"
      >
        <button
          type="button"
          onClick={() => onDone?.()}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
          aria-label="Close tour"
          data-testid="guided-tour-skip"
        >
          <X className="w-4 h-4" />
        </button>
        <p className="text-[11px] font-mono uppercase tracking-wider text-primary mb-1">
          {step.eyebrow || 'Quick tour'}
        </p>
        <h3 className="font-semibold text-base mb-1.5" data-testid="guided-tour-title">{step.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>

        <div className="flex items-center justify-between mt-4">
          <button
            type="button"
            onClick={() => onDone?.()}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <Button size="sm" variant="ghost" onClick={back} data-testid="guided-tour-back">Back</Button>
            )}
            <Button size="sm" variant="premium" onClick={next} data-testid="guided-tour-next">
              {isLast ? 'Got it' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedTour;
